import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useDispatch, useSelector } from "react-redux";
import { ResourceError } from "../../../../components/utils/ResourceError";
import {
  ArrowPathRoundedSquareIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import {
  mapErrors,
  updateUrlWithException,
} from "../../../../components/Utils";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { HistoriesModalButton } from "../../../../components/dashboard/histories/modal-button";
import {
  deleteServiceAgreement,
  fetchServiceAgreement,
  reactivateServiceAgreement,
  getPdfServiceAgreement,
} from "../../../../api/service/agreement";
import { ServiceAgreementInfo } from "../../../../components/dashboard/service/agreements/agreement-info";
import { ReportsStackList } from "../../../../components/dashboard/service/reports/reports-stack-list";
import { ServiceAgreementForm } from "../../../../components/dashboard/service/agreements/agreement-form";
import { mergeAgreementFormItem } from "../../../../components/dashboard/service/agreements/agreementFormInitialValues";
import { Notes } from "../../../../components/dashboard/notes/notes";
import { ServiceAgreementNewReportSection } from "../../../../components/dashboard/service/agreements/new-report-section";
import { ServiceAgreementChangeDateSection } from "../../../../components/dashboard/service/agreements/change-date-section";
import { ServiceAgreementStatus } from "../../../../components/dashboard/service/agreements/utils";
import SimplePrompt from "../../../../components/utils/simplePrompt";
import { ServiceAgreementOrderlines } from "../../../../components/dashboard/service/agreements/agreement-orderlines";
import { OrdersStacklist } from "../../../../components/dashboard/orders/orders-stack-list";
import { PMTabs } from "../../../../components/utils/PMTabs";
import { ServiceAgreementDocuments } from "./documents";
import { AgreementWorkflowPanel } from "../../../../components/dashboard/service/agreements/AgreementWorkflowPanel";
import { SigningPanel } from "../../../../components/dashboard/signing/SigningPanel";
import {
  resolveServiceAgreementPdfResult,
  serviceAgreementPdfActionLabel,
} from "../../../../components/dashboard/service/agreements/serviceAgreementPdfExport";

export const ServiceAgreement = (props) => {
  const { id } = useParams();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <AgreementPage key={`${companyId}:${id}`} {...props} />;
};

const AgreementPage = (props) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    agreement: {},
  });
  const [pdfExporting, setPdfExporting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // Deconstruct itemState (Store)
  const { isLoading, error, errors, agreement } = itemState;

  const item = agreement;

  const [selectedTab, setSelectedTab] = useState(
    parseInt(searchParams.get("tab")) || 0
  );

  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        tab: selectedTab,
      }
    );
  }, [selectedTab]);

  useEffect(() => {
    getAgreement();
  }, [dispatch]);

  const getAgreement = async () => {
    return fetchServiceAgreement(id)
      .then((res) => {
        if (res?.data?.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            agreement: res?.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = [t("service.agreements.actions.load_failed")];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          agreement: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const handleRefresh = (data) => {
    if (!data) return;
    setItemState((prevState) => ({
      ...prevState,
      agreement: mergeAgreementFormItem(prevState.agreement, data),
    }));
  };

  const [cancelPrompt, setCancelPrompt] = useState(false);
  const [agreementActing, setAgreementActing] = useState(false);

  const applyAgreementMutation = async ({ request, successKey, fallbackKey, closePrompt }) => {
    if (agreementActing || !item?.id) return;
    setAgreementActing(true);
    try {
      const response = await request({ id: item.id });
      const data = response?.data?.data;
      if (!data?.id || (Array.isArray(response?.data?.errors) && response.data.errors.length)) {
        throw new Error(response?.data?.errors?.filter(Boolean).join(", ") || t(fallbackKey));
      }
      toast.success(t(successKey));
      closePrompt(false);
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        agreement: mergeAgreementFormItem(prevState.agreement, data),
      }));
    } catch (error) {
      const message = error?.response?.data?.errors?.filter(Boolean).join(", ")
        || error?.response?.data?.problem?.message
        || error?.message
        || t(fallbackKey);
      toast.error(message);
    } finally {
      setAgreementActing(false);
    }
  };

  const cancelAgreement = async () => {
    await applyAgreementMutation({
      request: deleteServiceAgreement,
      successKey: "service.agreements.actions.cancelled",
      fallbackKey: "service.agreements.actions.cancel_failed",
      closePrompt: setCancelPrompt,
    });
  };

  const [reactivatePrompt, setReactivatePrompt] = useState(false);

  const reactivateAgreement = async () => {
    await applyAgreementMutation({
      request: reactivateServiceAgreement,
      successKey: "service.agreements.actions.reactivated",
      fallbackKey: "service.agreements.actions.reactivate_failed",
      closePrompt: setReactivatePrompt,
    });
  };

  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const exportPdf = async () => {
    if (!item?.id || pdfExporting) return;
    setPdfExporting(true);
    try {
      const response = await getPdfServiceAgreement({ id: item.id });
      const resolved = resolveServiceAgreementPdfResult(response);
      if (resolved.ok && resolved.blob) {
        const url = window.URL.createObjectURL(resolved.blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = resolved.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }
      if (resolved.ok && resolved.pdfUrl) {
        window.open(resolved.pdfUrl, "_blank", "noopener,noreferrer");
        return;
      }
      toast.error(t("service.agreements.actions.pdf_failed"));
    } catch (error) {
      toast.error(
        mapErrors(error?.errors) || t("service.agreements.actions.pdf_failed")
      );
    } finally {
      setPdfExporting(false);
    }
  };

  const tabs = [
    {
      name: t("service.agreements.tabs.details"),
      component: <ServiceAgreementInfo item={item} isLoading={isLoading} onRefresh={handleRefresh} />,
    },
    {
      name: t("service.agreements.tabs.edit"),
      component: (
        <ServiceAgreementForm
          item={item}
          onRefresh={(data) => {
            handleRefresh(data);
          }}
        />
      ),
    },
    {
      name: t("service.agreements.tabs.documents"),
      component: <ServiceAgreementDocuments moduleParam={item?.id} />,
    },
    {
      name: t("service.agreements.tabs.signing"),
      component: (
        <SigningPanel
          signableType="ServiceAgreement"
          signableId={item?.id}
          serviceAgreementId={item?.id}
          signingEnabled={item.status !== "cancelled" && !["awaiting_signature", "rejected"].includes(item.lifecycle_state)}
          onChanged={getAgreement}
          title={t("service.agreements.signing.document_title", {
            number: item?.agreement_number || item?.id || "",
          }).trim()}
          nonSensitiveTitle={t("service.agreements.signing.non_sensitive_title")}
          description={item?.description}
          defaultSigner={{
            name:
              item?.contacts?.find((row) => row.is_primary && row.party === "customer")?.displayTitle ||
              item?.contact?.displayTitle ||
              item?.customer?.displayTitle ||
              item?.customer?.name ||
              "",
            email:
              item?.contacts?.find((row) => row.is_primary && row.party === "customer")?.email ||
              item?.contact?.email ||
              item?.customer?.email ||
              "",
            mobile:
              item?.contacts?.find((row) => row.is_primary && row.party === "customer")?.mobile ||
              item?.contact?.mobile ||
              item?.contact?.phone ||
              item?.customer?.phone ||
              "",
          }}
        />
      ),
    },
    {
      name: t("service.agreements.tabs.orderlines"),
      component: <ServiceAgreementOrderlines item={item} />,
    },
    {
      name: t("service.agreements.tabs.reports"),
      component: (
        <ReportsStackList
          module_param={item?.id}
          module="agreement"
          filters={[
            {
              name: "agreement",
              value: item?.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("service.agreements.tabs.orders"),
      component: (
        <OrdersStacklist
          module_param={item?.id}
          module="agreement"
          filters={[
            {
              name: "agreement",
              value: item?.id,
            },
          ]}
        />
      ),
    },
  ];

  const headerActions = [
    {
      component: (
        <div className="px-2 mt-2">
          <Notes module={"service_agreement"} module_param={item?.id} />
        </div>
      ),
      label: t("service.agreements.header.notes"),
      description: "",
      path: null,
      action: null,
      textColor: "text-teal-700",
      bgColor: "bg-red-200",
      hidden: false,
      menu: false,
    },
    {
      component: (
        <HistoriesModalButton
          module={"service_agreement"}
          module_param={item?.id}
        />
      ),
      label: t("service.agreements.header.history"),
      description: "",
      path: null,
      action: null,
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
    },
    {
      label: serviceAgreementPdfActionLabel({ exporting: pdfExporting, t }),
      description: "",
      path: null,
      action: () => exportPdf(),
      icon: ArrowDownTrayIcon,
      textColor: "text-slate-700",
      bgColor: "bg-white",
      hidden: !item?.id,
      menu: false,
      loading: pdfExporting,
      disabled: pdfExporting,
    },
    {
      label: t("service.agreements.actions.cancel"),
      description: "",
      path: null,
      action: () => setCancelPrompt(true),
      icon: ExclamationTriangleIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: item?.status === "cancelled",
      menu: true,
    },
    {
      label: t("service.agreements.actions.reactivate"),
      description: "",
      path: null,
      action: () => setReactivatePrompt(true),
      icon: ExclamationTriangleIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: item?.status !== "cancelled",
      menu: true,
    },
  ];

  const headerMeta = [
    {
      component: <ServiceAgreementStatus serviceAgreement={agreement} />,
      label: t("service.agreements.header.status"),
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="">
          <ResourceLoading />
        </div>
      );
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    if (!item?.id) {
      return <ResourceEmpty />;
    }

    return (
      <>
        <DashboardHeader
          subtitle=""
          actions={headerActions}
          title={agreement?.displayTitle}
          meta={headerMeta}
          // mainButton={{
          //   label: "New",
          //   action: () => setCaseDialogOpen(true),
          //   path: null,
          //   color: "",
          //   icon: PlusIcon,
          // }}
        >
          <div className="mx-auto mt-8 grid max-w-7xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-6">
            <div className="space-y-6 lg:col-span-4 lg:col-start-1 sm:col-span-4">
              <PMTabs
                tabs={tabs}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
              />
              {(tabs[selectedTab] || tabs[0]).component}
            </div>
            <div className="space-y-6 lg:col-span-4 lg:col-start-5 sm:col-span-4">
              <AgreementWorkflowPanel item={agreement} onRefresh={handleRefresh} onDocuments={() => setSelectedTab(2)} onExportPdf={exportPdf} pdfExporting={pdfExporting} />
              {agreement.follow_up_enabled !== false && <ServiceAgreementNewReportSection item={agreement} />}
              {agreement.follow_up_enabled !== false && <ServiceAgreementChangeDateSection
                item={agreement}
                onRefresh={(data) => handleRefresh(data)}
              />}
            </div>
          </div>
        </DashboardHeader>

        <SimplePrompt
          title={t("service.agreements.actions.cancel_prompt_title")}
          description={t("service.agreements.actions.cancel_prompt_body")}
          open={cancelPrompt}
          onClose={() => setCancelPrompt(false)}
          onAccept={() => cancelAgreement()}
        />

        <SimplePrompt
          title={t("service.agreements.actions.reactivate_prompt_title")}
          description={t("service.agreements.actions.reactivate_prompt_body")}
          open={reactivatePrompt}
          onClose={() => setReactivatePrompt(false)}
          onAccept={() => reactivateAgreement()}
        />
      </>
    );
  };

  return <>{renderContent()}</>;
};
